import Big from 'big.js'
import { dates } from '@homevest/utils'
import type { OrphanedUtilityType } from './types'

export const DEFAULT_FEE_PERCENTAGE = '0.867'

const DECIMAL_INPUT_PATTERN = /^(?:\d+(?:\.\d*)?|\.\d+)$/

export const isStrictCalendarDate = (value?: string | null): value is string => {
  if (!value) {
    return false
  }

  try {
    return dates.normalizeToDateString(value) === value
  } catch (_error) {
    return false
  }
}

const getDecimalPlaces = (value: string) => value.split('.')[1]?.length ?? 0

const isValidDecimalInput = (value: string, maximumDecimalPlaces: number) => {
  const trimmedValue = value.trim()

  if (
    !trimmedValue ||
    !DECIMAL_INPUT_PATTERN.test(trimmedValue) ||
    getDecimalPlaces(trimmedValue) > maximumDecimalPlaces
  ) {
    return false
  }

  try {
    return new Big(trimmedValue).gte(0)
  } catch (_error) {
    return false
  }
}

export const getOriginalAmountError = (value: string) => {
  const trimmedValue = value.trim()

  if (!trimmedValue) {
    return 'Original bill amount is required.'
  }

  if (trimmedValue.startsWith('-')) {
    return 'Original bill amount must be greater than $0.00.'
  }

  if (!isValidDecimalInput(trimmedValue, 2) || !Number.isFinite(Number(trimmedValue))) {
    return 'Enter a dollar amount with at most 2 decimal places.'
  }

  return new Big(trimmedValue).gt(0) ? null : 'Original bill amount must be greater than $0.00.'
}

export const getFeePercentageError = (value: string) => {
  const trimmedValue = value.trim()

  if (!trimmedValue) {
    return 'Fee percentage is required.'
  }

  if (trimmedValue.startsWith('-')) {
    return 'Fee percentage must be between 0 and 100.'
  }

  if (!isValidDecimalInput(trimmedValue, 3)) {
    return 'Enter a percentage with at most 3 decimal places.'
  }

  return new Big(trimmedValue).lte(100) ? null : 'Fee percentage must be between 0 and 100.'
}

export const calculateChargebackAmount = (
  originalAmount: string,
  feePercentage: string
): string | null => {
  if (getOriginalAmountError(originalAmount) || getFeePercentageError(feePercentage)) {
    return null
  }

  return new Big(originalAmount.trim())
    .times(new Big(1).plus(new Big(feePercentage.trim()).div(100)))
    .round(2, Big.roundHalfUp)
    .toFixed(2)
}

export const getRequiredDateError = (value: string, label: string) => {
  if (!value) {
    return `${label} is required.`
  }

  return isStrictCalendarDate(value) ? null : `${label} must be a valid YYYY-MM-DD date.`
}

export const getServicePeriodError = ({
  finalLiabilityDate,
  occupancyDate,
  serviceEndDate,
  serviceStartDate
}: {
  finalLiabilityDate?: string | null
  occupancyDate: string
  serviceEndDate: string
  serviceStartDate: string
}) => {
  if (!isStrictCalendarDate(serviceStartDate) || !isStrictCalendarDate(serviceEndDate)) {
    return null
  }

  if (serviceStartDate > serviceEndDate) {
    return 'Service end date must be on or after the service start date.'
  }

  const startsAfterRental =
    isStrictCalendarDate(finalLiabilityDate) && serviceStartDate > finalLiabilityDate
  const endsBeforeRental = isStrictCalendarDate(occupancyDate) && serviceEndDate < occupancyDate

  if (startsAfterRental || endsBeforeRental) {
    const rentalEnd = isStrictCalendarDate(finalLiabilityDate)
      ? finalLiabilityDate
      : 'the current rental'

    return `Service period must overlap the rental period (${occupancyDate} through ${rentalEnd}).`
  }

  return null
}

export const isEligibleUtilityType = (utilityType: Pick<OrphanedUtilityType, 'liability_type'>) =>
  Boolean(
    utilityType.liability_type &&
      !utilityType.liability_type.deactivated_at &&
      utilityType.liability_type.is_variable
  )

export const getEligibleUtilityTypes = (utilityTypes: OrphanedUtilityType[]) =>
  utilityTypes
    .filter(isEligibleUtilityType)
    .sort((left, right) => left.name.localeCompare(right.name))

const getHttpStatus = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const directStatus =
    'statusCode' in error ? error.statusCode : 'status' in error ? error.status : undefined

  if (typeof directStatus === 'number' && directStatus >= 100) {
    return directStatus
  }

  if ('response' in error && error.response && typeof error.response === 'object') {
    const response = error.response
    const responseStatus = 'status' in response ? response.status : undefined

    return typeof responseStatus === 'number' && responseStatus >= 100 ? responseStatus : undefined
  }

  return undefined
}

export const isAmbiguousSubmissionError = (error: unknown) => {
  const status = getHttpStatus(error)

  return status === undefined || status === 0 || status >= 500
}

export const getOrphanedChargebackErrorMessage = (error: unknown) => {
  if (typeof error === 'string' && error) {
    return error
  }

  if (error && typeof error === 'object') {
    if ('response' in error && error.response && typeof error.response === 'object') {
      const responseData = 'data' in error.response ? error.response.data : undefined

      if (responseData && typeof responseData === 'object' && 'message' in responseData) {
        const responseMessage = responseData.message

        if (typeof responseMessage === 'string' && responseMessage) {
          return responseMessage
        }
      }
    }

    const message = 'message' in error ? error.message : undefined

    if (typeof message === 'string' && message) {
      return message
    }

    const errorLabel = 'error' in error ? error.error : undefined

    if (typeof errorLabel === 'string' && errorLabel) {
      return errorLabel
    }
  }

  return 'Unable to create orphaned utility chargeback.'
}
